/**
 * AI Assistant module shell — «دستیار هوشمند» (`/t/:slug/ai`, `/ai/wallet`,
 * `/ai/settings`).
 *
 * UI_REFERENCE_SOURCE: "Sakhtar AI Assistant.dc.html" (claude.ai/design project
 * «Cloud ERP»). The design's sidebar sub-items (گفتگوها / کیف پول توکن /
 * تنظیمات دستیار) are rendered here as the module's own tab strip so the
 * sidebar entry keeps its current place and order.
 *
 * This is a UI preview: no assistant, wallet or conversation backend exists.
 * The one real call is «هر وقت آماده شد به من اطلاع بده»
 * (`POST t/:slug/api/v1/ai-assistant/notify-me`), which records the caller's
 * interest for the operator. Everything else is sample data labelled «نمونه»;
 * the data scope comes from the caller's real capabilities.
 */
import { useState } from 'react';
import { Link, Outlet, useLocation, useParams } from 'react-router-dom';
import { V2Button, Icon } from '@sakhtar/design-system/react';
import { apiPost } from '../../lib/apiClient.js';
import { computeActiveTab } from '../../shell/modules/tabMatch.js';
import { AiPreviewProvider, useAiPreview, type AiForcedState } from './aiPreview.js';
import { AiConnectProvider } from './connect/AiProviderContext.js';
import { fetchTransport } from './connect/fetchTransport.js';
import { isAiLive } from './connect/mode.ts';
import type { AiProviderMode } from './connect/select.ts';
import './ai.css';

type NotifyState =
  | { readonly status: 'idle' }
  | { readonly status: 'pending' }
  | { readonly status: 'done'; readonly alreadyRegistered: boolean }
  | { readonly status: 'error' };

const FORCED_STATE_OPTIONS: readonly { value: AiForcedState; label: string }[] = [
  { value: 'none', label: 'حالت عادی' },
  { value: 'suspended', label: 'تننت معلق' },
  { value: 'not-permitted', label: 'کاربر مجاز نیست' },
  { value: 'error', label: 'خطای سرویس' },
];

export function AiLayout() {
  const { slug = '' } = useParams<{ slug: string }>();
  // Build-time choice (`VITE_AI_LIVE`); a live build never falls back to fixtures.
  const config: AiProviderMode = isAiLive()
    ? { mode: 'live', slug, transport: fetchTransport }
    : { mode: 'preview' };
  return (
    <AiPreviewProvider>
      <AiConnectProvider config={config}>
        <AiLayoutBody />
      </AiConnectProvider>
    </AiPreviewProvider>
  );
}

function AiLayoutBody() {
  const ai = useAiPreview();
  const location = useLocation();
  const [notify, setNotify] = useState<NotifyState>({ status: 'idle' });

  const base = `${ai.base}/ai`;
  const live = isAiLive();
  const tabs = [
    { id: 'chats', hrefSuffix: '', label: 'گفتگوها' },
    { id: 'documents', hrefSuffix: '/documents', label: 'دانش و اسناد سازمانی' },
    { id: 'wallet', hrefSuffix: '/wallet', label: 'کیف پول توکن' },
    ...(ai.isTenantAdmin
      ? [{ id: 'settings', hrefSuffix: '/settings', label: 'تنظیمات دستیار' }]
      : []),
  ];
  const activeTab = computeActiveTab(location.pathname, base, tabs);
  const onChats = activeTab.id === 'chats';

  async function handleNotifyMe() {
    setNotify({ status: 'pending' });
    try {
      const result = await apiPost<{ registered: boolean; alreadyRegistered: boolean }>(
        `${ai.base}/api/v1/ai-assistant/notify-me`,
        { displayName: ai.fullName },
      );
      setNotify({ status: 'done', alreadyRegistered: result.alreadyRegistered });
    } catch {
      setNotify({ status: 'error' });
    }
  }

  return (
    <div className="ai-module" data-ai-tab={activeTab.id}>
      {onChats ? null : (
        <div className="ai-module__head">
          <Link to={base} className="ai-back">
            <Icon name="chevronRight" size="sm" />
            بازگشت به گفتگوها
          </Link>
          <div className="sk-module-head">
            <span className="sk-module-mark" aria-hidden="true">
              <Icon name="star" size="md" />
            </span>
            <div className="ai-module__titles">
              <h1 className="sk-module-head__title">
                {tabs.find((tab) => tab.id === activeTab.id)?.label ?? 'دستیار هوشمند'}
              </h1>
              <div className="sk-module-head__subtitle">دستیار هوشمند</div>
            </div>
          </div>
        </div>
      )}

      {live ? null : (
        <>
          <div className="sk-alert sk-alert--info ai-notice">
            <Icon name="info" size="md" />
            <div className="sk-alert__body">
              <div className="sk-alert__title">این صفحه نمایشی است</div>
              <div className="ai-notice__text">
                دستیار هنوز به سرویس واقعی وصل نیست؛ گفتگوها، پاسخ‌ها، موجودی توکن و گزارش‌ها
                نمونه‌اند. محدودهٔ داده از دسترسی واقعی شما ساخته می‌شود.
              </div>
            </div>
            <div className="ai-notice__actions">
              <label className="ai-notice__state">
                <span>نمایش حالت</span>
                <select
                  className="sk-select"
                  value={ai.forcedState}
                  onChange={(event) => ai.setForcedState(event.target.value as AiForcedState)}
                  aria-label="نمایش حالت‌های مسدودکننده"
                >
                  {FORCED_STATE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              {notify.status === 'done' ? (
                <span className="ai-notice__done" role="status">
                  <Icon name="check" size="sm" />
                  {notify.alreadyRegistered
                    ? 'قبلاً ثبت شده‌اید؛ به‌محض آماده شدن اطلاع می‌دهیم'
                    : 'ثبت شد؛ به‌محض آماده شدن اطلاع می‌دهیم'}
                </span>
              ) : (
                <V2Button
                  variant="primary"
                  size="sm"
                  onClick={() => void handleNotifyMe()}
                  disabled={notify.status === 'pending' || ai.slug === ''}
                >
                  {notify.status === 'pending'
                    ? 'در حال ثبت...'
                    : 'هر وقت آماده شد به من اطلاع بده'}
                </V2Button>
              )}
            </div>
          </div>

          {notify.status === 'error' ? (
            <div className="sk-alert sk-alert--danger" role="alert">
              <Icon name="warn" size="md" />
              <div className="sk-alert__body">
                <div className="sk-alert__title">ثبت درخواست ناموفق بود</div>
                <div className="ai-notice__text">لطفاً دوباره تلاش کنید.</div>
              </div>
            </div>
          ) : null}
        </>
      )}

      <Outlet />
    </div>
  );
}
